import type { WorkspaceEntityStore, WorkspaceRuntime } from '#layers/runtime/public';
import type { PreparedWorkspaceRoutes } from './hydrate-workspace';
import { useOverlay } from '@nuxt/ui/composables';
import { useRouter } from '#app/composables/router';
import { useNuxtApp } from '#app/nuxt';
import { useAuth } from '#layers/access/public';
import { useWorkspaceApiBaseUrlFactory } from '#layers/base/public';
import { useSystemEntityStore, workspaceRuntime } from '#layers/runtime/public';
import { clearWorkspaceSpaceRoutes, replaceWorkspaceSpaceRoutes } from '#layers/spaces/public';
import { createWorkspaceRuntime } from './create-workspace-runtime';
import { hydrateWorkspace } from './hydrate-workspace';

/**
 * Connects the runtime state machine to Studio's routing. Candidate hydration receives its runtime
 * explicitly; publication swaps the dynamic routes, and the `published` transition that follows makes
 * the candidate the runtime every store derives from.
 */
export function useWorkspaceSwitch() {
	const router = useRouter();
	const nuxtApp = useNuxtApp();
	const systemStore = useSystemEntityStore();
	const auth = useAuth();
	const createWorkspaceBaseUrl = useWorkspaceApiBaseUrlFactory();
	const overlay = useOverlay();

	function switchTo(workspaceSlug: string) {
		return workspaceRuntime.switchRuntime({
			createRuntime: (slug) => createWorkspaceRuntime({ baseURL: createWorkspaceBaseUrl(slug), slug }),
			prepare: prepareCandidate,
			publish: publishRoutes,
			slug: workspaceSlug,
		});
	}

	function prepareCandidate(runtime: WorkspaceRuntime<WorkspaceEntityStore>) {
		const userId = auth.currentUser?.id;
		if (!userId) throw new Error('A signed-in user is required to prepare a workspace');

		return hydrateWorkspace({
			runtime,
			systemStore,
			translate: (key) => nuxtApp.$i18n.t(key),
			userId,
		});
	}

	/**
	 * The one thing a store cannot derive from the runtime: the route table.
	 * Publication is synchronous with no awaits and no expected failures (ADR 049). A throw is a bug and fails
	 * closed: the manager disposes both runtimes, so unpublishing leaves the routes as empty as the stores.
	 */
	function publishRoutes({ routes }: PreparedWorkspaceRoutes) {
		try {
			overlay.closeAll();
			replaceWorkspaceSpaceRoutes({ router, routes });
		}
		catch (error) {
			unpublishRoutes();
			throw error;
		}
	}

	function unpublishRoutes() {
		clearWorkspaceSpaceRoutes(router);
	}

	function leave() {
		overlay.closeAll();
		workspaceRuntime.leaveWorkspace({ unpublish: unpublishRoutes });
	}

	return { switchTo, leave };
}
